import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { auth } from "@/lib/api";
import { useSession } from "@/lib/session";
import { useTheme } from "@/lib/theme";

/**
 * Who is signed in, on every signed-in screen.
 *
 * The photo the provider gave us, or the person's initials on a brand disc
 * when it gave none. Tapping it opens the short list of things that are
 * about the account rather than about any pool: account settings, the admin
 * console for admins, and the way out. Renders nothing when there is no
 * session, which is how the component tests that mount screens without one
 * stay unaffected.
 */
export default function AccountMenu() {
  const { status } = useSession();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointer(event: PointerEvent) {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const user = status.user;
  if (!user) return null;
  const name = user.display_name?.trim() || user.email;

  async function signOut() {
    await auth.logout();
    window.location.assign("/");
  }

  return (
    <div ref={root} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account: ${name}`}
        className="flex items-center gap-2 rounded-full py-0.5 pr-2 pl-0.5 text-sm hover:bg-(--color-surface-sunken)"
      >
        <Avatar name={name} src={user.avatar_url} />
        <span className="hidden max-w-32 truncate sm:block">{name}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden className="text-(--color-ink-faint)">
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Account"
          className="card absolute right-0 z-30 mt-2 w-60 overflow-hidden py-1 shadow-lg"
        >
          <div className="border-b border-(--color-border-subtle) px-4 py-2.5">
            <p className="truncate text-sm font-medium">{name}</p>
            {user.display_name && (
              <p className="truncate text-xs text-(--color-ink-muted)">{user.email}</p>
            )}
          </div>
          <MenuLink to="/account" onClick={() => setOpen(false)}>
            Account settings
          </MenuLink>
          {user.is_admin && (
            <MenuLink to="/admin" onClick={() => setOpen(false)}>
              Site admin
            </MenuLink>
          )}
          {theme.choice && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                theme.set(null);
                setOpen(false);
              }}
              className="block w-full px-4 py-2.5 text-left text-sm hover:bg-(--color-surface-sunken)"
            >
              Match the system theme
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => void signOut()}
            className="block w-full px-4 py-2.5 text-left text-sm hover:bg-(--color-surface-sunken)"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function MenuLink({
  to,
  onClick,
  children,
}: {
  to: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      role="menuitem"
      onClick={onClick}
      className="block px-4 py-2.5 text-sm hover:bg-(--color-surface-sunken)"
    >
      {children}
    </Link>
  );
}

/** The provider's picture, or initials. 32px, so it fits the header's baseline row. */
export function Avatar({
  name,
  src,
  size = 32,
}: {
  name: string;
  src: string | null;
  size?: number;
}) {
  const [broken, setBroken] = useState(false);
  if (src && !broken) {
    return (
      <img
        src={src}
        alt=""
        width={size}
        height={size}
        // Google's photo CDN refuses requests that carry a referrer.
        referrerPolicy="no-referrer"
        onError={() => setBroken(true)}
        className="shrink-0 rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      aria-hidden
      className="flex shrink-0 items-center justify-center rounded-full bg-(--color-brand) font-semibold text-(--color-on-brand)"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
    >
      {initials(name)}
    </span>
  );
}

/** "Jason Kringen" → "JK"; "jason@example.com" → "J". */
export function initials(name: string): string {
  const words = name
    .split("@")[0]
    .split(/[\s._-]+/)
    .filter(Boolean);
  if (words.length === 0) return "?";
  const letters = words.length === 1 ? words[0].slice(0, 1) : words[0].slice(0, 1) + words[words.length - 1].slice(0, 1);
  return letters.toUpperCase();
}
